// ============================================================
// data/constants.js
// Datos estáticos: impresoras 3D predefinidas y catálogo de monedas
// ============================================================

/**
 * Impresoras 3D predefinidas con sus especificaciones técnicas.
 * La potencia se almacena en Watts; la conversión a kW se hace en tiempo real.
 */
export const PRINTERS_CATALOG = [
  {
    id: 'creality_k1c',
    brand: 'Creality',
    model: 'K1C',
    powerWatts: 350,      // Potencia máxima en Watts
    powerKw: 0.35,        // Potencia en kW (calculado)
    type: 'FDM',
    description: 'Impresora FDM de alta velocidad con extrusor directo',
  },
  {
    id: 'snapmaker_u1',
    brand: 'Snapmaker',
    model: 'U1',
    powerWatts: 1150,
    powerKw: 1.15,
    type: 'FDM Multifunción',
    description: 'Sistema 3-en-1: impresión, CNC y láser',
  },
  {
    id: 'bambu_x1c',
    brand: 'Bambu Lab',
    model: 'X1 Carbon',
    powerWatts: 1000,
    powerKw: 1.0,
    type: 'FDM Multi-material',
    description: 'Impresora de alta velocidad con LIDAR',
  },
  {
    id: 'prusa_mk4',
    brand: 'Prusa',
    model: 'MK4',
    powerWatts: 360,
    powerKw: 0.36,
    type: 'FDM',
    description: 'Impresora open-source de referencia',
  },
  {
    id: 'elegoo_saturn3',
    brand: 'Elegoo',
    model: 'Saturn 3 Ultra',
    powerWatts: 220,
    powerKw: 0.22,
    type: 'MSLA/Resina',
    description: 'Impresora de resina gran formato 12K',
  },
  {
    id: 'custom',
    brand: 'Personalizada',
    model: 'Custom',
    powerWatts: 0,
    powerKw: 0,
    type: 'Personalizada',
    description: 'Ingresa tus propios datos de potencia',
  },
];

/**
 * Tipos de filamento comunes con sus costos estimados por kg.
 * Los precios son referenciales; el usuario puede modificarlos.
 */
export const FILAMENT_TYPES = [
  { id: 'pla',      name: 'PLA',          description: 'Ácido poliláctico - Más común'      },
  { id: 'pla_plus', name: 'PLA+',         description: 'PLA mejorado - Mayor resistencia'    },
  { id: 'abs',      name: 'ABS',          description: 'Acrilonitrilo Butadieno Estireno'    },
  { id: 'petg',     name: 'PETG',         description: 'Tereftalato de polietileno glicolado'},
  { id: 'tpu',      name: 'TPU',          description: 'Poliuretano termoplástico flexible'  },
  { id: 'asa',      name: 'ASA',          description: 'Resistente UV - Uso exterior'        },
  { id: 'nylon',    name: 'Nylon',        description: 'Alta resistencia y flexibilidad'     },
  { id: 'resin',    name: 'Resina (MSLA)', description: 'Fotopolímero para impresoras SLA'   },
  { id: 'carbon',   name: 'Carbono',      description: 'Fibra de carbono - Ultra resistente' },
  { id: 'other',    name: 'Otro',         description: 'Material personalizado'              },
];

/**
 * Catálogo de monedas del mundo con sus datos de formato.
 * Se incluyen 14 monedas de países hispanohablantes y principales economías.
 *
 * @property {string} code    - Código ISO 4217
 * @property {string} symbol  - Símbolo tipográfico
 * @property {string} name    - Nombre completo de la moneda
 * @property {string} country - País o región de origen
 * @property {number} decimals - Número de decimales estándar
 */
/**
 * Catálogo de monedas: USD, EUR y toda Latinoamérica.
 * La opción 'OTRA' permite al usuario ingresar nombre y símbolo personalizados.
 */
export const CURRENCIES = [
  // ---- Dólar y Euro (internacionales de referencia) ----
  {
    code: 'USD',
    symbol: '$',
    name: 'Dólar Americano',
    country: 'Estados Unidos',
    decimals: 2,
    flag: '🇺🇸',
    referenceKwh: 0.13,
  },
  {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    country: 'Unión Europea',
    decimals: 2,
    flag: '🇪🇺',
    referenceKwh: 0.28,
  },
  // ---- Latinoamérica (orden alfabético por país) ----
  {
    code: 'ARS',
    symbol: '$',
    name: 'Peso Argentino',
    country: 'Argentina',
    decimals: 0,
    flag: '🇦🇷',
    referenceKwh: 42.5,
  },
  {
    code: 'BOB',
    symbol: 'Bs.',
    name: 'Boliviano',
    country: 'Bolivia',
    decimals: 2,
    flag: '🇧🇴',
    referenceKwh: 0.45,
  },
  {
    code: 'BRL',
    symbol: 'R$',
    name: 'Real Brasileño',
    country: 'Brasil',
    decimals: 2,
    flag: '🇧🇷',
    referenceKwh: 0.75,
  },
  {
    code: 'CLP',
    symbol: '$',
    name: 'Peso Chileno',
    country: 'Chile',
    decimals: 0,
    flag: '🇨🇱',
    referenceKwh: 120.0,
  },
  {
    code: 'COP',
    symbol: '$',
    name: 'Peso Colombiano',
    country: 'Colombia',
    decimals: 0,
    flag: '🇨🇴',
    referenceKwh: 740.25,
  },
  {
    code: 'CRC',
    symbol: '₡',
    name: 'Colón Costarricense',
    country: 'Costa Rica',
    decimals: 0,
    flag: '🇨🇷',
    referenceKwh: 85.0,
  },
  {
    code: 'CUP',
    symbol: '$',
    name: 'Peso Cubano',
    country: 'Cuba',
    decimals: 2,
    flag: '🇨🇺',
    referenceKwh: 0.09,
  },
  {
    code: 'DOP',
    symbol: 'RD$',
    name: 'Peso Dominicano',
    country: 'República Dominicana',
    decimals: 2,
    flag: '🇩🇴',
    referenceKwh: 8.5,
  },
  {
    code: 'GTQ',
    symbol: 'Q',
    name: 'Quetzal Guatemalteco',
    country: 'Guatemala',
    decimals: 2,
    flag: '🇬🇹',
    referenceKwh: 1.2,
  },
  {
    code: 'HNL',
    symbol: 'L',
    name: 'Lempira Hondureño',
    country: 'Honduras',
    decimals: 2,
    flag: '🇭🇳',
    referenceKwh: 3.5,
  },
  {
    code: 'MXN',
    symbol: '$',
    name: 'Peso Mexicano',
    country: 'México',
    decimals: 2,
    flag: '🇲🇽',
    referenceKwh: 1.05,
  },
  {
    code: 'NIO',
    symbol: 'C$',
    name: 'Córdoba Nicaragüense',
    country: 'Nicaragua',
    decimals: 2,
    flag: '🇳🇮',
    referenceKwh: 4.2,
  },
  {
    code: 'PAB',
    symbol: 'B/.',
    name: 'Balboa Panameño',
    country: 'Panamá',
    decimals: 2,
    flag: '🇵🇦',
    referenceKwh: 0.17,
  },
  {
    code: 'PYG',
    symbol: '₲',
    name: 'Guaraní Paraguayo',
    country: 'Paraguay',
    decimals: 0,
    flag: '🇵🇾',
    referenceKwh: 320.0,
  },
  {
    code: 'PEN',
    symbol: 'S/',
    name: 'Sol Peruano',
    country: 'Perú',
    decimals: 2,
    flag: '🇵🇪',
    referenceKwh: 0.56,
  },
  {
    code: 'UYU',
    symbol: '$',
    name: 'Peso Uruguayo',
    country: 'Uruguay',
    decimals: 2,
    flag: '🇺🇾',
    referenceKwh: 6.8,
  },
  {
    code: 'VES',
    symbol: 'Bs.',
    name: 'Bolívar Venezolano',
    country: 'Venezuela',
    decimals: 2,
    flag: '🇻🇪',
    referenceKwh: 0.03,
  },
  // ---- Opción personalizada ----
  {
    code: 'OTRA',
    symbol: '',
    name: 'Otra moneda...',
    country: 'Personalizada',
    decimals: 2,
    flag: '🌐',
    referenceKwh: 0,
  },
];

/**
 * Mensaje de WhatsApp por defecto.
 * Contiene placeholders que se sustituyen antes del envío:
 *   {{CLIENTE}}   - Nombre del cliente
 *   {{FIGURA}}    - Nombre de la figura/pieza
 *   {{PRECIO}}    - Precio final con moneda
 *   {{TIEMPO}}    - Tiempo de impresión
 *   {{MATERIAL}}  - Tipo de filamento
 *   {{PESO}}      - Peso en gramos
 */
export const DEFAULT_WHATSAPP_MESSAGE = `¡Hola {{CLIENTE}}! 👋

Te envío la cotización para tu figura *{{FIGURA}}*:

📋 *DETALLE DE LA COTIZACIÓN:*
• Material: {{MATERIAL}}
• Peso estimado: {{PESO}} g
• Tiempo de impresión: {{TIEMPO}} h
• Precio del filamento: {{COSTO_MATERIAL}}

⚡ Electricidad: {{COSTO_LUZ}}
🔧 Mantenimiento + Depreciación: {{COSTO_ADICIONAL}}
👷 Mano de obra: {{MANO_OBRA}}

💰 *PRECIO FINAL: {{PRECIO}}*

Este precio incluye un margen del {{MARGEN}}%.

¡Cualquier pregunta estoy disponible! 😊`;
